import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDMY } from "@/lib/utils";
import { DateField } from "@/components/DateField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ShiftAssignments, shiftText } from "@/components/ShiftAssignments";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Loader2, CheckCircle2, XCircle, Save, Users, Eye, ArrowLeft, Zap, AlertTriangle } from "lucide-react";

function todayISO() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" });
}

export default function SendPage() {
  const [settings, setSettings] = useState(null);
  const [targetDate, setTargetDate] = useState(todayISO());
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState([]);
  const [custom, setCustom] = useState([]);
  const [assign, setAssign] = useState({});
  const [roleNames, setRoleNames] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [allowResend, setAllowResend] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const matchIds = (recips, roles) => recips.filter((r) => roles.includes(r.role)).map((r) => r.id);
  const toggleShift = (key) => setSelected((x) => (x.includes(key) ? x.filter((k) => k !== key) : [...x, key]));
  const toggleStaff = (key, id) => setAssign((a) => {
    const cur = a[key] || [];
    return { ...a, [key]: cur.includes(id) ? cur.filter((i) => i !== id) : [...cur, id] };
  });
  const addCustom = (c) => {
    const key = `custom-${Date.now()}`;
    setCustom((x) => [...x, { ...c, key, custom: true }]);
    setAssign((a) => ({ ...a, [key]: matchIds(preview.recipients, c.roles) }));
    setSelected((x) => [...x, key]);
  };
  const removeCustom = (key) => { setCustom((x) => x.filter((c) => c.key !== key)); setSelected((x) => x.filter((k) => k !== key)); };

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/roles").then((r) => setRoleNames(r.data.map((x) => x.name)));
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  const doPreview = async () => {
    setPreviewing(true);
    setResult(null);
    setPreview(null);
    setCustom([]);
    setAllowResend(false);
    try {
      const { data } = await api.post("/send/preview", { target_date: targetDate });
      setPreview(data);
      setSelected(data.shift_options.map((o) => o.id));
      setAssign(Object.fromEntries(data.shift_options.map((o) => [o.id, matchIds(data.recipients, o.roles)])));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setPreviewing(false); }
  };

  const confirmSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const assignments = chosenShifts.map((s) => (s.custom
        ? { start_time: s.start_time, staff_ids: assign[s.key] || [] }
        : { shift_id: s.id, staff_ids: assign[s.key] || [] }));
      const { data } = await api.post("/send", { target_date: targetDate, assignments, allow_resend: allowResend });
      setResult(data);
      setPreview(null);
      toast.success(`${data.delivered} delivered, ${data.undelivered} undelivered${data.simulated ? " (simulated)" : ""}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (!settings) return <div className="text-muted-foreground">Loading…</div>;

  const allShifts = [...(preview?.shift_options || []).map((o) => ({ ...o, key: o.id })), ...custom];
  const chosenShifts = allShifts.filter((s) => selected.includes(s.key) && (assign[s.key] || []).length).sort((a, b) => a.start_time.localeCompare(b.start_time));
  const chosenText = chosenShifts.map(shiftText).join(" & ");
  const people = [...new Set(chosenShifts.flatMap((s) => assign[s.key] || []))];
  const sampleId = people[0];
  const sampleRecipient = preview?.recipients?.find((r) => r.id === sampleId);
  const sampleName = sampleRecipient?.staff_name || "Jake";
  const sampleDay = preview?.day || new Date(targetDate + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long" });
  const sampleShift = !preview ? "06:00 HQ Packing" : sampleId
    ? chosenShifts.filter((s) => (assign[s.key] || []).includes(sampleId))
      .map((s) => `${s.start_time} ${sampleRecipient.role}`).join(" & ")
    : "[choose a shift]";
  const smsText = (settings.message_template || "")
    .replaceAll("{name}", sampleName)
    .replaceAll("{day}", sampleDay)
    .replaceAll("{date}", fmtDMY(targetDate))
    .replaceAll("{shift}", sampleShift)
    .replaceAll("{note}", settings.note || "");
  const smsSegments = Math.max(1, Math.ceil(smsText.length / 160));
  const sendCount = people.length;

  return (
    <div className="animate-fade-up">
      <p className="uppercase text-xs tracking-[0.3em] text-muted-foreground mb-2">Alerts</p>
      <h1 className="font-head text-4xl sm:text-5xl font-800 tracking-tight leading-none">Send Alerts</h1>
      <p className="text-muted-foreground mt-3 mb-8">Review who will be texted, then push shift reminders for a day.</p>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6 min-w-0">
          <h3 className="font-head text-lg font-700 mb-1">Manual push</h3>
          <p className="text-sm text-muted-foreground mb-5">Pick the date, choose the shifts and who's working each one, then confirm.</p>

          {!preview && !result && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Shift date</Label>
                <DateField value={targetDate} onChange={setTargetDate} testid="send-date-input" className="mt-1.5" />
              </div>
              <Button onClick={doPreview} disabled={previewing} data-testid="preview-button"
                className="w-full gap-2 transition-transform duration-200 hover:-translate-y-0.5">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                Review recipients
              </Button>
            </div>
          )}

          {preview && (
            <div className="space-y-4" data-testid="preview-panel">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-head font-700">{preview.day}, {fmtDMY(preview.target_date)}</p>
                  <p className="text-xs text-muted-foreground break-words">{chosenShifts.length ? `Sending: ${chosenText}` : "Tick a shift with at least one person"}</p>
                </div>
                <Badge className="rounded-full bg-primary gap-1 shrink-0"><Users className="h-3 w-3" /> {sendCount}</Badge>
              </div>
              {preview.exception && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/60 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="exception-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>This date is marked as <b>{preview.exception.reason}</b> ({preview.exception.kind === "holiday" ? "public holiday" : "business closure"}). Auto-send is off for this day, but you can still send manually.</span>
                </div>
              )}
              {preview.previous_send && (
                <div className="rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 text-xs space-y-2" data-testid="already-sent-warning">
                  <p className="flex items-start gap-2 text-destructive">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{preview.previous_send.count} alert{preview.previous_send.count === 1 ? " was" : "s were"} already sent for this date (last at {new Date(preview.previous_send.last_sent_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}).</span>
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer font-medium">
                    <Checkbox checked={allowResend} onCheckedChange={(v) => setAllowResend(!!v)} data-testid="allow-resend-checkbox" />
                    Send again anyway
                  </label>
                </div>
              )}
              <ShiftAssignments shifts={allShifts} selected={selected} assign={assign} recipients={preview.recipients} roleNames={roleNames}
                onToggleShift={toggleShift} onToggleStaff={toggleStaff} onAdd={addCustom} onRemove={removeCustom} />

              {preview.skipped?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold">Skipped:</span> {preview.skipped.map((s) => `${s.staff_name} (${s.reason})`).join(", ")}
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setPreview(null)} data-testid="preview-back-button" className="gap-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                <Button onClick={() => setConfirmOpen(true)} disabled={sending || sendCount === 0 || chosenShifts.length === 0 || (preview.previous_send && !allowResend)} data-testid="confirm-send-button"
                  className="flex-1 gap-2 transition-transform duration-200 hover:-translate-y-0.5">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send to {sendCount}
                </Button>
              </div>

              <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="send-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{preview.previous_send ? "Resend" : "Send"} {sendCount} text{sendCount === 1 ? "" : "s"}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Shift {chosenText} on {preview.day} {fmtDMY(preview.target_date)}.{preview.exception ? ` Note: this is ${preview.exception.reason}.` : ""}{preview.simulated ? " (Simulation mode, no real texts.)" : " Real SMS will be sent and charged."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="send-confirm-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={confirmSend} data-testid="send-confirm-yes">Yes, send now</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="send-result">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className="rounded-full bg-[hsl(var(--success))]">{result.delivered} delivered</Badge>
                <Badge className="rounded-full bg-destructive">{result.undelivered} undelivered</Badge>
                {result.simulated && <Badge variant="outline" className="rounded-full">Simulated</Badge>}
              </div>
              <div className="space-y-1.5 max-h-64 overflow-auto">
                {result.messages.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm" data-testid={`result-msg-${m.id}`}>
                    <span className="font-medium">{m.staff_name}</span>
                    <span className={`inline-flex items-center gap-1.5 ${["delivered","sent","queued","accepted"].includes(m.status) ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                      {["delivered","sent","queued","accepted"].includes(m.status) ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                      {m.status}
                    </span>
                  </div>
                ))}
              </div>
              <Button variant="secondary" onClick={() => { setResult(null); }} data-testid="send-again-button" className="w-full">Send another</Button>
            </div>
          )}
        </Card>

        <Card className="p-6 min-w-0">
          <h3 className="font-head text-lg font-700 mb-1">Default settings</h3>
          <p className="text-sm text-muted-foreground mb-5">Stored preferences for scheduled sends and message content.</p>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Automatic sending</p>
                  <p className="text-xs text-muted-foreground">{isAdmin ? "Auto-send at the default time each day (turn off for manual only)." : "Auto-send, send time and timing can only be changed by an admin."}</p>
                </div>
              </div>
              <Switch disabled={!isAdmin} checked={settings.automation_enabled} onCheckedChange={(v) => setSettings({ ...settings, automation_enabled: v })} data-testid="setting-automation-switch" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Default send time</Label>
                <Input type="time" disabled={!isAdmin} value={settings.default_send_time} onChange={(e) => setSettings({ ...settings, default_send_time: e.target.value })} data-testid="setting-time-input" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Alert for</Label>
                <Select disabled={!isAdmin} value={settings.send_target} onValueChange={(v) => setSettings({ ...settings, send_target: v })}>
                  <SelectTrigger data-testid="setting-target-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same_day">Same day's shift</SelectItem>
                    <SelectItem value="next_day">Next day's shift</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Message template</Label>
              <Textarea rows={3} value={settings.message_template} onChange={(e) => setSettings({ ...settings, message_template: e.target.value })} data-testid="setting-template-input" className="mt-1.5" />
              <p className="text-xs text-muted-foreground mt-1.5">Placeholders: {"{name} {day} {date} {shift} {note}"}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Note</Label>
              <Input value={settings.note} onChange={(e) => setSettings({ ...settings, note: e.target.value })} data-testid="setting-note-input" className="mt-1.5" />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">SMS preview</Label>
                <span className="text-[10px] text-muted-foreground" data-testid="sms-char-count">{smsText.length} chars · {smsSegments} SMS</span>
              </div>
              <div className="mt-1.5 rounded-xl bg-muted/50 border border-border p-4">
                <p className="text-[10px] font-semibold text-muted-foreground text-center mb-3">Montek Co &amp; PL</p>
                <div className="flex justify-start">
                  <div data-testid="sms-preview-bubble" className="max-w-[85%] bg-card border border-border rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm text-foreground shadow-sm whitespace-pre-wrap break-words">
                    {smsText || "Your message will appear here…"}
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground text-center mt-3">
                  Sample for {sampleName} · {sampleDay} {fmtDMY(targetDate)}. Review recipients to preview a real staff member.
                </p>
              </div>
            </div>

            <Button onClick={saveSettings} disabled={savingSettings} variant="secondary" data-testid="save-settings-button" className="gap-2">
              {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
